import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { Crown, AlertCircle } from 'lucide-react';

const SignupPage = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    ageVerified: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.ageVerified) {
      setError('You must be 18 or older to sign up');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    const result = await signup(formData);
    setLoading(false);

    if (result.success) {
      const promo = sessionStorage.getItem('pendingPromoCode');
      navigate(promo ? `/tiers?code=${encodeURIComponent(promo)}` : '/feed');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-black via-gray-900 to-amber-950/20 relative overflow-hidden">
      {/* Film grain texture */}
      <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxwYXRoIGQ9Ik0wIDBoMzAwdjMwMEgweiIgZmlsdGVyPSJ1cmwoI2EpIiBvcGFjaXR5PSIuMDUiLz48L3N2Zz4=')]" style={{backgroundSize: '150px'}}></div>
      
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
        className="w-full max-w-md"
      >
        <Card className="bg-gray-900/95 border-2 border-amber-900/50 shadow-[0_0_30px_rgba(251,191,36,0.2)] backdrop-blur relative">
          {/* Top secret stamp */}
          <motion.div 
            className="absolute -top-4 -right-4 bg-red-700 text-white rounded-full w-20 h-20 flex items-center justify-center border-4 border-red-800 transform rotate-12 font-headline text-xs z-10"
            initial={{ scale: 0, rotate: 45 }}
            animate={{ scale: 1, rotate: 12 }}
            transition={{ delay: 0.3, duration: 0.4, type: "spring", stiffness: 200 }}
          >
            <div className="text-center">
              <div>NEW</div>
              <div className="text-xl">🔐</div>
              <div>CASE</div>
            </div>
          </motion.div>

          <CardHeader className="space-y-1 text-center border-b border-amber-900/30 pb-6">
            <motion.div 
              className="flex flex-col items-center mb-2"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.4 }}
            >
              <img 
                src="/logo.png" 
                alt="Deadline by Daria" 
                className="h-12 w-auto mb-2"
                style={{ 
                  mixBlendMode: 'screen',
                  filter: 'drop-shadow(0 0 10px rgba(220, 38, 38, 0.7)) drop-shadow(0 0 20px rgba(220, 38, 38, 0.4))'
                }}
                data-testid="signup-logo"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.4 }}
            >
              <CardTitle className="text-2xl font-headline text-amber-500 tracking-wider">NEW INVESTIGATION</CardTitle>
              <CardDescription className="text-gray-400 font-typewriter text-sm">
                Create your detective profile to access case files
              </CardDescription>
            </motion.div>
          </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="displayName">Display Name</Label>
              <Input
                id="displayName"
                placeholder="Enter your display name"
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Create a password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
              />
            </div>

            <div className="flex items-start space-x-2 p-4 border rounded-lg bg-muted/50">
              <Checkbox
                id="ageVerified"
                checked={formData.ageVerified}
                onCheckedChange={(checked) => setFormData({ ...formData, ageVerified: checked })}
              />
              <div className="space-y-1">
                <Label htmlFor="ageVerified" className="text-sm font-medium leading-none">
                  I am 18 years or older and agree to the{' '}
                  <a href="/terms" target="_blank" className="text-amber-500 hover:text-amber-400 underline">Terms of Service</a>
                </Label>
                <p className="text-xs text-muted-foreground">
                  All content is fictional and for entertainment purposes only. You must be at least 18 years old to register.
                </p>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4 border-t border-amber-900/30 pt-6">
            <motion.div 
              className="w-full"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider transition-all hover:shadow-[0_0_20px_rgba(251,191,36,0.4)]" disabled={loading}>
                {loading ? 'OPENING CASE FILE...' : 'OPEN CASE FILE'}
              </Button>
            </motion.div>
            <p className="text-sm text-center text-gray-400 font-typewriter">
              Already registered?{' '}
              <Link to="/login" className="text-amber-500 hover:text-amber-400 hover:underline transition-colors">
                Access existing case
              </Link>
            </p>
          </CardFooter>
        </form>
        </Card>
      </motion.div>
    </div>
  );
};

export default SignupPage;